import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PaymentLinkShell } from '../components/paymentLinks/PaymentLinkShell';
import { fetchBoothTarget } from '../services/bookingService';
import { BookingNotice } from './BookingPage';

/**
 * PD-17649 — `/book/booth` and `/book/booth/:kit`: the lasting links on printed pieces.
 * They go straight to the show we're at, and ask only when two run at once.
 */
export function BoothLinkPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { kit } = useParams();

	const { data, isLoading } = useQuery({
		queryKey: ['booth-link', kit ?? null],
		queryFn: () => fetchBoothTarget(kit ?? null),
		retry: false,
	});

	useEffect(() => {
		if (data?.path != null) {
			void navigate(data.path, { replace: true });
		}
	}, [data?.path, navigate]);

	if (isLoading || data?.path != null) {
		return <PaymentLinkShell secureLabel={null} showSignIn={false}><div className="min-h-64" /></PaymentLinkShell>;
	}

	if (data == null || data.shows.length === 0) {
		return <BookingNotice title="No booth booking right now" body="Check back before the next show." />;
	}

	return (
		<PaymentLinkShell secureLabel={null} showSignIn={false}>
			<div className="space-y-4" data-testid="booth-choose">
				<h1 className="text-xl font-bold text-default">{t('Which show are you at?')}</h1>
				{data.shows.map((show) => (
					<Link key={show.path} to={show.path} className="block bg-brand-surface border border-brand-border rounded-xl p-5 hover:border-portal-accent">
						<h2 className="font-semibold text-default">{t(show.title)}</h2>
						<p className="text-sm text-brand-muted">{t(show.line)}</p>
					</Link>
				))}
			</div>
		</PaymentLinkShell>
	);
}
